import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { logout } from '../../features/auth/authSlice';
import { toggleThemeMode } from '../../app/slices/uiSlice';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Menu, MenuItem, IconButton, Avatar, Tooltip, Divider, Box, Typography,
    ListItemIcon, ListItemText, Button, useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LanguageSwitcher from './LanguageSwitcher';

const UserMenu: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    const { isAuthenticated, user } = useAppSelector((state) => state.auth);
    const { themeMode } = useAppSelector((state) => state.ui);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isOpen = Boolean(anchorEl);

    // Handle opening the menu
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle closing the menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Handle user logout
    const handleLogout = () => {
        dispatch(logout());
        handleClose();
        // Navigate to home page after logout.
        navigate('/');
    };

    // Handle theme mode toggle
    const handleThemeToggle = () => {
        dispatch(toggleThemeMode());
    };

    // Navigate to settings page when avatar is clicked
    const handleAvatarClick = () => {
        handleClose();
        navigate('/settings');
    };

    const commonMenuItems = [
        { textKey: 'about_us', to: '/about', icon: <InfoIcon fontSize="small" /> },
        { textKey: 'terms_of_service', to: '/terms', icon: <GavelIcon fontSize="small" /> },
        { textKey: 'privacy_policy', to: '/privacy', icon: <PrivacyTipIcon fontSize="small" /> },
        { textKey: 'contact_us', to: '/contact', icon: <ContactMailIcon fontSize="small" /> },
    ];

    // Render common menu items (About, Terms, Privacy, Contact)
    const renderCommonMenuItems = () => (
        commonMenuItems.map((item) => (
            <MenuItem
                key={item.textKey}
                component={RouterLink}
                to={item.to}
                onClick={handleClose}
                sx={{ py: 1 }} // Add spacing around menu items
            >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText>{t(item.textKey)}</ListItemText>
            </MenuItem>
        ))
    );

    // Render theme and language switcher options for all devices
    const renderThemeAndLanguageOptions = () => (
        <>
            <Divider sx={{ my: 1 }} /> {/* Add spacing around divider */}
            <MenuItem
                onClick={handleThemeToggle}
                sx={{ py: 1 }} // Add spacing around menu item
            >
                <ListItemIcon>
                    {themeMode === 'light' ? <Brightness4Icon fontSize="small" /> : <Brightness7Icon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{t(themeMode === 'light' ? 'switch_to_dark' : 'switch_to_light')}</ListItemText>
            </MenuItem>
            <MenuItem
                onClick={(e) => e.stopPropagation()}
                sx={{ justifyContent: "space-between", py: 1 }} // Add spacing around menu item
            >
                <ListItemText>{t('language_switcher_title')}</ListItemText>
                <LanguageSwitcher />
            </MenuItem>
        </>
    );

    if (isAuthenticated && user) {
        // --- Logged-in state: Show User Avatar ---
        return (
            <>
                <Tooltip title={t('settings')}>
                    <IconButton onClick={handleOpen} size="small" sx={{ ml: 2 }}>
                        <Avatar
                            src={user.avatarUrl}
                            sx={{ width: 32, height: 32, cursor: 'pointer' }}
                        >
                            {/* Fallback to first letter of name or a generic icon */}
                            {!user.avatarUrl && (user.name ? user.name.charAt(0).toUpperCase() : <AccountCircleIcon/>)}
                        </Avatar>
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchorEl}
                    open={isOpen}
                    onClose={handleClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                        sx: {
                            width: 312.5, // Increased width by 25% (250 * 1.25 = 312.5)
                            borderRadius: 2,
                            boxShadow: 3,
                            p: 1 // Add padding to menu content
                        }
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Avatar
                            src={user.avatarUrl}
                            sx={{ width: 60, height: 60, mb: 1, cursor: 'pointer' }}
                            onClick={handleAvatarClick} // Make avatar clickable
                        >
                            {!user.avatarUrl && (user.name ? user.name.charAt(0).toUpperCase() : <AccountCircleIcon fontSize="large"/>)}
                        </Avatar>
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis' }} // Prevent overflow
                        >
                            {user.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis' }} // Prevent overflow
                        >
                            {user.email}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} /> {/* Add spacing around divider */}
                    <MenuItem
                        component={RouterLink}
                        to="/settings"
                        onClick={handleClose}
                        sx={{ py: 1 }} // Add spacing around menu item
                    >
                        <ListItemIcon><SettingsIcon fontSize="small"/></ListItemIcon>
                        <ListItemText>{t('settings')}</ListItemText>
                    </MenuItem>
                    <Divider sx={{ my: 1 }} /> {/* Add spacing around divider */}
                    {/* Added common menu items for logged-in users */}
                    {renderCommonMenuItems()}
                    {/* Theme and language options for all devices */}
                    {renderThemeAndLanguageOptions()}
                    <Divider sx={{ my: 1 }} /> {/* Add spacing around divider */}
                    <MenuItem
                        onClick={handleLogout}
                        sx={{ color: 'error.main', py: 1 }} // Add spacing around menu item
                    >
                        <ListItemIcon><LogoutIcon fontSize="small" color="error"/></ListItemIcon>
                        <ListItemText>{t('logout')}</ListItemText>
                    </MenuItem>
                </Menu>
            </>
        );
    } else {
        // --- Logged-out state: Show Menu Icon ---
        return (
            <>
                <Tooltip title={t('more_options')}>
                    <IconButton color="inherit" onClick={handleOpen}>
                        <MenuIcon />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchorEl}
                    open={isOpen}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            width: 312.5, // Increased width by 25% (250 * 1.25 = 312.5)
                            borderRadius: 2,
                            boxShadow: 3,
                            p: 1 // Add padding to menu content
                        }
                    }}
                >
                    {/* Add "Not logged in" text at the top */}
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5">{t('not_logged_in')}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} /> {/* Add spacing around divider */}
                    {renderCommonMenuItems()}
                    {/* Theme and language options for all devices */}
                    {renderThemeAndLanguageOptions()}
                    {/* Login/Signup buttons at the bottom of the menu */}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-around', gap: 1 }}>
                        <Button component={RouterLink} to="/login" variant="outlined" fullWidth onClick={handleClose}>
                            {t('login')}
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/signup"
                            variant="contained"
                            fullWidth
                            disableElevation
                            onClick={handleClose}
                            sx={{
                                bgcolor: 'inverse.main',
                                color: 'inverse.contrastText',
                                '&:hover': { bgcolor: 'inverse.hover.main' },
                            }}
                        >
                            {t('signup')}
                        </Button>
                    </Box>
                </Menu>
            </>
        );
    }
};

export default UserMenu;